// Live free-slot availability for the heatmap's "Available" view mode.
//
// Where `useSlotDefinitions` reads the recurring slot *templates*, this hook
// reads `/api/calendar/calendar` with `includeSlots: true` and
// `includeNotAvailableSlots: false` — so only open (unbooked) slot occurrences
// for the visible week come back. It shapes them into the SAME
// `{ groups: [{ key, label, rows }] }` structure `useSlotDefinitions` returns,
// so the matrix and detail rail render it unchanged. Each row is a concrete
// occurrence (no `recurrence` mask) — it lands on its own weekday.

import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { fetchCalendarData } from "@/lib/api/calendar";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import {
  subscribeAppointmentChanges,
  subscribeAppointmentRefresh,
} from "@/lib/realtime/appointment-realtime-bridge";

const UNASSIGNED_KEY = "__unassigned__";

function trimmed(value) {
  return typeof value === "string" ? value.trim() : "";
}

function indexFromOptions(options) {
  const map = new Map();
  if (Array.isArray(options)) {
    for (const option of options) {
      const id = trimmed(option?.id);
      const name = trimmed(option?.name);
      if (id && name) map.set(id, name);
    }
  }
  return map;
}

function resolveDoctorName(entity, doctorId, doctorIndex) {
  if (entity && typeof entity === "object") {
    const full = `${trimmed(entity.firstName)} ${trimmed(entity.lastName)}`.trim();
    if (full) return full;
    const display = trimmed(entity.displayName);
    if (display) return display;
  }
  return (doctorId && doctorIndex.get(doctorId)) || "";
}

function resolveTenantName(entity, tenantId, tenantIndex) {
  if (entity && typeof entity === "object") {
    const name = resolveTenantDisplayName(entity);
    if (name) return name;
  }
  return (tenantId && tenantIndex.get(tenantId)) || "";
}

// One `/api/calendar/calendar` slot occurrence → a heatmap row. Mirrors the row
// shape `mapApiItemToDefinitionRow` produces; `recurrence` stays null because
// availability rows are concrete single-day occurrences.
function mapAvailabilityRow(item, doctorIndex, tenantIndex) {
  if (!item || trimmed(item.kind) !== "slot") return null;

  const startStr = trimmed(item.start) || trimmed(item.time?.start);
  const endStr = trimmed(item.end) || trimmed(item.time?.end);
  if (!startStr || !endStr) return null;
  const startAt = DateTime.fromISO(startStr, { setZone: true }).toLocal();
  const endAt = DateTime.fromISO(endStr, { setZone: true }).toLocal();
  if (!startAt.isValid || !endAt.isValid) return null;

  const id = trimmed(item.id) || trimmed(item._id);
  let definitionId = trimmed(item.mongoSlotId);
  if (!definitionId && id.startsWith("s-")) {
    const parts = id.split("-");
    if (parts.length >= 2) definitionId = parts[1];
  }
  if (!definitionId) definitionId = id;

  const doctorId = trimmed(item.doctorId) || undefined;
  const tenantId = trimmed(item.tenantId) || undefined;
  const doctorName = resolveDoctorName(item._doctor, doctorId, doctorIndex);
  const tenantName = resolveTenantName(item._tenant, tenantId, tenantIndex);

  return {
    id: id || definitionId,
    definitionId,
    mongoSlotId: definitionId,
    kind: "slot",
    title: trimmed(item.title) || trimmed(item.placeholder) || "Slot",
    placeholder: trimmed(item.placeholder),
    slotType: trimmed(item.slotType) || "static",
    startAt,
    endAt,
    doctorId,
    doctor: doctorName || undefined,
    doctorShort: doctorName || undefined,
    tenantId,
    tenantName: tenantName || undefined,
    _tenant: item._tenant
      ? { ...item._tenant, ...(tenantName ? { name: tenantName } : {}) }
      : tenantName
        ? { name: tenantName }
        : undefined,
    colorHex: trimmed(item?.metadata?.colorHex) || undefined,
    recurrence: null,
    occurrenceInRange: 1,
    status: trimmed(item.status) || undefined,
    raw: item,
  };
}

function rowMatchesSearch(row, query) {
  if (!query) return true;
  return [row.title, row.placeholder, row.doctor, row._tenant?.name]
    .map(trimmed)
    .filter(Boolean)
    .join(" · ")
    .toLowerCase()
    .includes(query);
}

// Resolve a row's group identity for either dimension.
function rowGroupKey(row, groupBy) {
  if (groupBy === "tenant") {
    const id = trimmed(row.tenantId);
    if (!id) return { key: UNASSIGNED_KEY, label: "Unassigned" };
    const label = trimmed(row._tenant?.name) || trimmed(row.tenantName) || "Clinic";
    return { key: id, label };
  }
  const id = trimmed(row.doctorId);
  if (!id) return { key: UNASSIGNED_KEY, label: "Unassigned" };
  const label = trimmed(row.doctor) || trimmed(row.doctorShort) || "Doctor";
  return { key: id, label };
}

// Bucket rows into one group per primary entity (doctor or tenant); groups and
// rows both sorted.
function groupRowsBy(rows, groupBy) {
  const map = new Map();
  for (const row of rows) {
    const { key, label } = rowGroupKey(row, groupBy);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { key, label, rows: [] };
      map.set(key, bucket);
    }
    bucket.rows.push(row);
  }
  const groups = Array.from(map.values());
  for (const group of groups) {
    group.rows.sort(
      (a, b) => (a.startAt?.toMillis?.() ?? 0) - (b.startAt?.toMillis?.() ?? 0),
    );
  }
  groups.sort((a, b) => {
    if (a.key === UNASSIGNED_KEY) return 1;
    if (b.key === UNASSIGNED_KEY) return -1;
    return a.label.localeCompare(b.label);
  });
  return groups;
}

export function useAvailableSlots({
  fromDate,
  toDate,
  search,
  groupBy = "doctor",
  refreshKey = 0,
  doctorOptions,
  tenantOptions,
  enabled = true,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bridgeVersion, setBridgeVersion] = useState(0);

  // Stay current on any cross-component slot change (see `useSlotDefinitions`
  // for why both bridge channels are subscribed).
  useEffect(() => {
    const bump = () => setBridgeVersion((value) => value + 1);
    const unsubscribeRefresh = subscribeAppointmentRefresh(bump);
    const unsubscribeChanges = subscribeAppointmentChanges(bump);
    return () => {
      unsubscribeRefresh();
      unsubscribeChanges();
    };
  }, []);

  const rangeKey = useMemo(() => {
    const f = fromDate?.toUTC?.()?.toISO?.() || "";
    const t = toDate?.toUTC?.()?.toISO?.() || "";
    return `${enabled ? 1 : 0}|${f}|${t}|${refreshKey}|${bridgeVersion}`;
  }, [enabled, fromDate, toDate, refreshKey, bridgeVersion]);

  useEffect(() => {
    if (!enabled) return;
    if (!fromDate?.isValid || !toDate?.isValid || toDate <= fromDate) return;

    let isMounted = true;
    setIsLoading(true);
    setError(null);
    fetchCalendarData({
      scope: "date",
      from: fromDate.toUTC().toISO(),
      to: toDate.toUTC().toISO(),
      includeSlots: true,
      includeNotAvailableSlots: false,
    })
      .then((response) => {
        if (!isMounted) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setItems([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const doctorIndex = useMemo(() => indexFromOptions(doctorOptions), [doctorOptions]);
  const tenantIndex = useMemo(() => indexFromOptions(tenantOptions), [tenantOptions]);

  const groups = useMemo(() => {
    const query = trimmed(search).toLowerCase();
    const rows = items
      .map((item) => mapAvailabilityRow(item, doctorIndex, tenantIndex))
      .filter(Boolean)
      .filter((row) => rowMatchesSearch(row, query));
    return groupRowsBy(rows, groupBy);
  }, [items, search, doctorIndex, tenantIndex, groupBy]);

  return { groups, isLoading: enabled && isLoading, error };
}
